import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, RotateCcw, History, Edit3, Save, X,
  Heart, Wind, Thermometer, Gauge, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";

const VITAL_META = {
  bp_systolic:       { label: "BP Systolic",      unit: "mmHg", icon: Heart,       accent: "#DC2626" },
  bp_diastolic:      { label: "BP Diastolic",     unit: "mmHg", icon: Heart,       accent: "#DC2626" },
  heart_rate:        { label: "Heart Rate",       unit: "bpm",  icon: Activity,    accent: "#EF4444" },
  spo2:              { label: "SpO₂",             unit: "%",    icon: Wind,        accent: "#0EA5E9" },
  temperature:       { label: "Temperature",      unit: "°C",   icon: Thermometer, accent: "#F59E0B" },
  respiratory_rate:  { label: "Respiratory Rate", unit: "rpm",  icon: Gauge,       accent: "#8B5CF6" },
};

const SOURCE_BADGE = {
  patient:  { label: "Custom",   bg: "#FEE2E2", text: "#991B1B" },
  facility: { label: "Facility", bg: "#E0E7FF", text: "#3730A3" },
  global:   { label: "System",   bg: "#F1F5F9", text: "#475569" },
  default:  { label: "Default",  bg: "#F1F5F9", text: "#475569" },
};

const BANDS = [
  { key: "lo_critical", label: "Critical (low)",  hint: "≤ this value → CRITICAL",  swatch: "#DC2626" },
  { key: "lo_moderate", label: "Moderate (low)",  hint: "≤ this value → MODERATE", swatch: "#F59E0B" },
  { key: "normal_min",  label: "Stable min",      hint: "Stable band starts here",  swatch: "#10B981" },
  { key: "normal_max",  label: "Stable max",      hint: "Stable band ends here",    swatch: "#10B981" },
  { key: "hi_moderate", label: "Moderate (high)", hint: "> stable_max → MODERATE",  swatch: "#F59E0B" },
  { key: "hi_critical", label: "Critical (high)", hint: "> this value → CRITICAL",  swatch: "#DC2626" },
];

function nice(v) { return v === null || v === undefined ? "—" : v; }

export function ThresholdsTab({ patientId, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {vital_type, bands, justification, _isReset}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, h] = await Promise.all([
        api.get(`/rpm/patients/${patientId}/thresholds`),
        api.get(`/rpm/patients/${patientId}/thresholds/history`),
      ]);
      setItems(t.data.items || []);
      setHistory(h.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  function startEdit(item) {
    setEditing({
      vital_type: item.vital_type,
      bands: { ...item.effective },
      justification: "",
      _isReset: false,
    });
  }

  function startReset(item) {
    setEditing({
      vital_type: item.vital_type,
      bands: { ...item.default },
      justification: "",
      _isReset: true,
    });
  }

  async function save() {
    if (!editing) return;
    if (!editing.justification.trim()) {
      toast.error("Justification is required");
      return;
    }
    try {
      if (editing._isReset) {
        await api.delete(`/rpm/patients/${patientId}/thresholds/${editing.vital_type}`, {
          params: { justification: editing.justification.trim() },
        });
        toast.success(`${VITAL_META[editing.vital_type]?.label} reset to default`);
      } else {
        const payload = { justification: editing.justification.trim() };
        BANDS.forEach((b) => {
          const v = editing.bands?.[b.key];
          if (v !== null && v !== "" && v !== undefined) payload[b.key] = parseFloat(v);
        });
        await api.put(`/rpm/patients/${patientId}/thresholds/${editing.vital_type}`, payload);
        toast.success(`${VITAL_META[editing.vital_type]?.label} threshold updated`);
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading) return <div className="p-6 text-sm text-[#6B7280]">Loading thresholds…</div>;

  return (
    <div data-testid="cl-thresholds-tab">
      <div className="mb-3">
        <h3 className="font-semibold text-[#1F2937]">Per-Patient Vital Thresholds</h3>
        <p className="text-xs text-[#6B7280] mt-0.5">
          Customize the stable / moderate / critical ranges for this patient. Defaults apply unless overridden.
          Every change requires a clinical justification and is audited.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((it) => {
          const meta = VITAL_META[it.vital_type] || {};
          const Icon = meta.icon || Activity;
          const src = SOURCE_BADGE[it.source] || SOURCE_BADGE.default;
          return (
            <div
              key={it.vital_type}
              className={`hah-card p-3.5 ${it.is_overridden ? "border-l-4 border-l-[#DC2626]" : ""}`}
              data-testid={`cl-threshold-card-${it.vital_type}`}
            >
              <div className="flex items-start justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <span
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${meta.accent}14`, color: meta.accent }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="font-semibold text-[#1F2937]">{meta.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                      {meta.unit}
                    </div>
                  </div>
                </div>
                <span
                  className="px-2 py-0.5 rounded text-[10px] uppercase font-bold"
                  style={{ background: src.bg, color: src.text }}
                  data-testid={`cl-threshold-source-${it.vital_type}`}
                >
                  {src.label}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {BANDS.map((b) => (
                  <div key={b.key} className="text-center" title={b.hint}>
                    <div className="w-full h-1 rounded mb-1" style={{ background: b.swatch }} />
                    <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">{b.label.split(" ")[0]}</div>
                    <div className="text-sm font-bold text-[#1F2937]">{nice(it.effective[b.key])}</div>
                  </div>
                ))}
              </div>

              {it.is_overridden && it.updated_by_name && (
                <div className="text-[10px] text-[#6B7280] mb-2 border-t border-[#E5E7EB] pt-1.5">
                  Last changed by <span className="font-semibold text-[#1F2937]">{it.updated_by_name}</span>{" "}
                  {it.updated_at && (
                    <> · {new Date(it.updated_at).toLocaleString()}</>
                  )}
                  {it.last_justification && (
                    <div className="italic mt-0.5 text-[#475569]">"{it.last_justification}"</div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-1.5">
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startEdit(it)}
                    className="border-[#E5E7EB] h-7 text-xs"
                    data-testid={`cl-threshold-edit-${it.vital_type}`}
                  >
                    <Edit3 className="w-3 h-3 mr-1" /> Customize
                  </Button>
                )}
                {!readOnly && it.is_overridden && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startReset(it)}
                    className="border-[#E5E7EB] h-7 text-xs text-[#991B1B]"
                    data-testid={`cl-threshold-reset-${it.vital_type}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Reset to default
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* History section */}
      <div className="hah-card mt-4 overflow-hidden" data-testid="cl-threshold-history">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#E5E7EB] bg-[#F5F7FA]">
          <History className="w-4 h-4 text-[#0B7E8C]" />
          <h4 className="font-semibold text-[#1F2937] text-sm">Change History</h4>
          <span className="text-xs text-[#6B7280]">{history.length} entries</span>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-xs text-[#6B7280]">
            No threshold changes yet for this patient.
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[#FAFBFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Vital</th>
                <th className="text-left p-2">Action</th>
                <th className="text-left p-2">By</th>
                <th className="text-left p-2">Before → After</th>
                <th className="text-left p-2">Justification</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => {
                const meta = VITAL_META[h.vital_type] || {};
                return (
                  <tr key={h.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`cl-threshold-history-row-${h.id}`}>
                    <td className="p-2 text-xs text-[#6B7280] whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
                    <td className="p-2 text-xs font-semibold text-[#1F2937]">{meta.label || h.vital_type}</td>
                    <td className="p-2">
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold"
                        style={{
                          background: h.action === "RESET" ? "#E0E7FF" : h.action === "CREATE" ? "#FEE2E2" : "#FEF3C7",
                          color:      h.action === "RESET" ? "#3730A3" : h.action === "CREATE" ? "#991B1B" : "#92400E",
                        }}
                      >
                        {h.action}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-[#1F2937]">{h.actor_name}<br/><span className="text-[10px] text-[#6B7280]">{h.actor_role}</span></td>
                    <td className="p-2 text-[11px] text-[#475569] font-mono">
                      <div>before: {JSON.stringify(h.before)}</div>
                      <div>after:  {JSON.stringify(h.after)}</div>
                    </td>
                    <td className="p-2 text-xs italic text-[#475569] max-w-xs">"{h.justification}"</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="cl-threshold-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing?._isReset ? "Reset to Default" : "Customize"} —{" "}
              {VITAL_META[editing?.vital_type]?.label}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              {editing._isReset && (
                <div className="border-l-4 border-l-[#3730A3] bg-[#E0E7FF] p-2 text-xs text-[#3730A3] rounded">
                  This will remove the patient-specific override and revert to the default range:
                  <div className="font-mono mt-1">{JSON.stringify(editing.bands)}</div>
                </div>
              )}
              {!editing._isReset && (
                <div className="grid grid-cols-3 gap-2">
                  {BANDS.map((b) => (
                    <FormField key={b.key} label={b.label}>
                      <Input
                        type="number"
                        step="0.1"
                        value={editing.bands?.[b.key] ?? ""}
                        onChange={(e) => setEditing({ ...editing, bands: { ...editing.bands, [b.key]: e.target.value } })}
                        placeholder="—"
                        data-testid={`cl-threshold-band-${b.key}`}
                      />
                    </FormField>
                  ))}
                </div>
              )}
              <FormField label="Clinical Justification (required)">
                <Textarea
                  rows={3}
                  value={editing.justification}
                  onChange={(e) => setEditing({ ...editing, justification: e.target.value })}
                  placeholder="e.g. Known COPD with chronic baseline SpO2 around 92%, accept lower critical threshold for this patient."
                  data-testid="cl-threshold-justification"
                />
              </FormField>
              <div className="border-l-4 border-l-[#F59E0B] bg-[#FEF3C7] p-2 text-[11px] text-[#92400E] rounded">
                <strong>Audit notice.</strong> This change is permanently recorded with your name,
                timestamp, and justification, and applied to all future readings for this patient only.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-threshold-save">
              <Save className="w-4 h-4 mr-1.5" />
              {editing?._isReset ? "Reset & log change" : "Save with justification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
